"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.candidateViewModel = candidateViewModel;
exports.getCandidateAnswerComparison = getCandidateAnswerComparison;
exports.hasDirectAnswers = hasDirectAnswers;
const data_fetching_1 = require("../data-fetching/index.js");
function getCandidateDisplayName(candidate, personsMap, organizationsMap) {
    if (candidate.displayName) {
        return candidate.displayName;
    }
    const firstReference = candidate.references?.[0];
    if (firstReference) {
        if (firstReference.type === "person") {
            return personsMap.get(firstReference.id)?.displayName;
        }
        if (firstReference.type === "organization") {
            return organizationsMap.get(firstReference.id)?.displayName;
        }
    }
    return undefined;
}
function getCandidateOrganization(candidate, personsMap, organizationsMap) {
    const firstReference = candidate.references?.[0];
    if (firstReference?.type === "person") {
        const person = personsMap.get(firstReference.id);
        if (person?.memberOf && person.memberOf.length > 0) {
            const firstOrganizationId = person.memberOf[0]?.id;
            return firstOrganizationId ? organizationsMap.get(firstOrganizationId)?.displayName : undefined;
        }
    }
    return undefined;
}
function getCandidateAvatar(candidate, personsMap, organizationsMap, baseUrl) {
    const avatar = (0, data_fetching_1.findImageByType)(candidate.images, "avatar");
    if (avatar)
        return { type: "avatar", urls: (0, data_fetching_1.resolveImageUrls)(avatar.urls, baseUrl) };
    const logo = (0, data_fetching_1.findImageByType)(candidate.images, "logo");
    if (logo)
        return { type: "logo", urls: (0, data_fetching_1.resolveImageUrls)(logo.urls, baseUrl) };
    const portrait = (0, data_fetching_1.findImageByType)(candidate.images, "portrait");
    if (portrait)
        return { type: "portrait", urls: (0, data_fetching_1.resolveImageUrls)(portrait.urls, baseUrl) };
    const firstReference = candidate.references?.[0];
    if (!firstReference)
        return undefined;
    if (firstReference.type === "person") {
        return personsMap.get(firstReference.id)?.avatar;
    }
    if (firstReference.type === "organization") {
        return organizationsMap.get(firstReference.id)?.avatar;
    }
    return undefined;
}
function getCandidateType(candidate) {
    const firstReference = candidate.references?.[0];
    if (!firstReference)
        return undefined;
    if (firstReference.type === "person")
        return "person";
    if (firstReference.type === "organization")
        return "organization";
    return undefined;
}
function candidateViewModel(candidate, personsMap, organizationsMap, baseUrl) {
    const displayName = getCandidateDisplayName(candidate, personsMap, organizationsMap);
    const organization = getCandidateOrganization(candidate, personsMap, organizationsMap);
    const avatar = getCandidateAvatar(candidate, personsMap, organizationsMap, baseUrl);
    const type = getCandidateType(candidate);
    const nestedCandidates = candidate.nestedCandidates?.map((nested) => candidateViewModel(nested, personsMap, organizationsMap, baseUrl));
    return {
        ...candidate,
        displayName,
        organization,
        avatar,
        type,
        nestedCandidates,
    };
}
function getCandidateAnswerComparison(candidateId, userAnswers, candidatesAnswers, questions = []) {
    const candidateAnswers = candidatesAnswers[candidateId] || [];
    const expertAnswers = candidatesAnswers.expert || [];
    // Create maps for quick lookup
    const userAnswersMap = new Map(userAnswers.map((answer) => [answer.questionId, answer]));
    const candidateAnswersMap = new Map(candidateAnswers.map((answer) => [answer.questionId, answer]));
    const expertAnswersMap = new Map(expertAnswers.map((answer) => [answer.questionId, answer]));
    // Iterate over the original questions array to preserve order
    return questions.map((question) => {
        const questionId = question.id;
        const userAnswer = userAnswersMap.get(questionId);
        const candidateAnswer = candidateAnswersMap.get(questionId);
        const expertAnswer = expertAnswersMap.get(questionId);
        return {
            questionId,
            questionText: question?.statement || question?.title || questionId,
            userAnswer: userAnswer?.answer,
            candidateAnswer: candidateAnswer?.answer,
            candidateComment: candidateAnswer?.comment,
            candidateSources: candidateAnswer?.sources,
            expertAnswer: expertAnswer?.answer,
            expertComment: expertAnswer?.comment,
            expertSources: expertAnswer?.sources,
            isImportant: userAnswer?.isImportant,
        };
    });
}
function hasDirectAnswers(candidateId, candidatesAnswers) {
    return candidateId in candidatesAnswers && (candidatesAnswers[candidateId]?.length ?? 0) > 0;
}
